import { Resource, ResourceRepository } from '@telespot/domain';

/**
 * Flat, UI-ready projection of a resource together with its direct children.
 *
 * Children are projected to `{ id, name }` (the same shape produced by the
 * retrieve-resource-children query) because the resource read hydrates a single
 * nesting level. `version` is coerced to a number to smooth over legacy data
 * that mixes string and numeric versions.
 *
 * @group Protocols
 * @category Payloads
 */
export interface RetrieveResourceDetailOutput {
  id: string;
  name: string;
  type: string;
  version: number;
  description: string;
  value: string;
  displayName: Record<string, string>;
  metadata: Record<string, unknown>;
  children: { id: string; name: string }[];
}

/**
 * Token-style error codes thrown by the retrieve-resource-detail query.
 *
 * Following the documented command/query error pattern: the enum value is
 * a stable namespaced identifier callers can disambiguate without parsing
 * free-form messages.
 */
export enum RetrieveResourceDetailErrorCode {
  RESOURCE_NOT_FOUND = 'retrieve_resource_detail.resource_not_found',
  REPOSITORY_ERROR = 'retrieve_resource_detail.repository_error',
}

/**
 * Thrown when retrieving a resource detail fails.
 *
 * `details` carries structured context (e.g. `{ resourceId }`) so callers
 * can render or log the failure without scraping the message.
 */
export class RetrieveResourceDetailException extends Error {
  constructor(
    public readonly code: RetrieveResourceDetailErrorCode,
    public readonly details: Record<string, string> = {}
  ) {
    super(code.toString());
    this.name = 'RetrieveResourceDetailException';
    Object.setPrototypeOf(this, RetrieveResourceDetailException.prototype);
  }
}

/**
 * Use case to retrieve a resource together with its direct children.
 */
export interface IRetrieveResourceDetailQuery {
  /**
   * Retrieve the resource and project it to a flat, UI-ready shape.
   * @param resourceId The id of the resource to retrieve.
   * @returns The resource metadata as flat fields plus its direct children as `{ id, name }`.
   * @throws RetrieveResourceDetailException when the operation fails.
   */
  execute(resourceId: string): Promise<RetrieveResourceDetailOutput>;
}

export class RetrieveResourceDetailQuery implements IRetrieveResourceDetailQuery {
  constructor(private readonly resourceRepository: ResourceRepository) {}

  public async execute(resourceId: string): Promise<RetrieveResourceDetailOutput> {
    try {
      const resource = await this.resourceRepository.getById(resourceId);
      return toOutput(resource);
    } catch (error) {
      throw toException(error, resourceId);
    }
  }
}

function toException(error: unknown, resourceId: string): RetrieveResourceDetailException {
  if (isNotFoundError(error)) {
    return new RetrieveResourceDetailException(
      RetrieveResourceDetailErrorCode.RESOURCE_NOT_FOUND,
      { resourceId }
    );
  }
  return new RetrieveResourceDetailException(
    RetrieveResourceDetailErrorCode.REPOSITORY_ERROR,
    { resourceId }
  );
}

function isNotFoundError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return /object not found/i.test(error.message);
}

function toOutput(resource: Resource): RetrieveResourceDetailOutput {
  const version = Number(resource.version);
  return {
    id: resource.id,
    name: resource.name,
    type: resource.type,
    version: Number.isNaN(version) ? 1 : version,
    description: resource.description ?? '',
    value: resource.value ?? '',
    displayName: { ...(resource.displayName ?? {}) },
    metadata: { ...(resource.metadata ?? {}) },
    children: (resource.resources ?? []).map((child: Resource) => ({
      id: child.id,
      name: child.name,
    })),
  };
}
